import { useState, useEffect, useContext, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { CreditCard, Loader2, Wallet } from "lucide-react";
import { API_BASE } from "../../shared/Constants";
import { PaymentContext } from "../../contexts/PaymentContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { reportHandledError, reportApiError } from "../../utils/reportError";
import MinimumAmountHint from "./MinimumAmountHint";
import { formatAmountInput } from "../../utils/currency";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string).catch(
  (err) => {
    console.warn("Failed to load Stripe.js:", err?.message || err);
    return null;
  }
);

const ELEMENT_UNAVAILABLE_MESSAGE =
  "Não foi possível carregar o formulário de pagamento. Recarregue a página e tente novamente.";

const ELEMENT_REMOUNTED_MESSAGE =
  "O formulário de pagamento foi recarregado. Tente novamente.";

// The Payment Element's DOM can be destroyed after it reports ready — the
// insertBefore breadcrumbs on PLATAFORMA-N point at extensions/auto-translate
// rewriting the DOM under React. The root cause (Chrome's page translation
// re-parenting text nodes and react-dom throwing on the next commit) is
// neutralised by src/googleTranslateGuard.ts; this remount stays as a
// last-resort fallback for anything else that tears the element down. It
// rebuilds the iframe against the same (still valid) intent, but a hostile
// environment can tear it down again, so self-healing is capped before
// falling back to asking for a full page reload.
const MAX_ELEMENT_REMOUNTS = 2;

// Stripe rejects confirmSetup/confirmPayment with an IntegrationError when the
// Payment Element is not mounted at confirm time. Its message is English-only
// and tells the user nothing actionable, so it is translated before display.
const isElementNotMountedError = (err: any) =>
  /elements should have a mounted (?:Payment Element|Express Checkout Element)/i.test(
    err?.message || ""
  );

// Keep the intent request and Stripe iframe loading stages the same size.
const PAYMENT_ELEMENT_MIN_HEIGHT = "min-h-[320px]";

function PaymentFieldsSkeleton() {
  return (
    <div role="status" aria-label="Carregando formulário de pagamento" className="flex flex-col gap-4">
      <span className="sr-only">Carregando formulário de pagamento</span>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-11 w-full" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-11" />
        <Skeleton className="h-11" />
      </div>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-11 w-full" />
    </div>
  );
}

export interface CheckoutProps {
  setup: boolean;
}

function CheckoutForm({
  setup,
  onComplete,
  onError,
  onElementLost,
  userEmail,
}: {
  setup: boolean;
  onComplete: () => void;
  onError: (msg: string) => void;
  onElementLost: () => boolean;
  userEmail?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  // The Payment Element mounts asynchronously (and can fail to mount at all,
  // e.g. behind an ad blocker). Confirming before it is mounted throws an
  // IntegrationError, so submission stays blocked until Stripe says it is up.
  const [elementReady, setElementReady] = useState(false);
  const [elementFailed, setElementFailed] = useState(false);
  const canSubmit = Boolean(stripe && elements && elementReady && !elementFailed);

  const handleLoadError = (event: any) => {
    setElementFailed(true);
    reportHandledError(event?.error ?? new Error("PaymentElement failed to load"), {
      category: "payment",
      feature: "checkout.elementLoad",
    });
    onError(ELEMENT_UNAVAILABLE_MESSAGE);
  };

  const recoverLostElement = () => {
    if (onElementLost()) {
      setElementReady(false);
      onError(ELEMENT_REMOUNTED_MESSAGE);
      return;
    }

    setElementFailed(true);
    onError(ELEMENT_UNAVAILABLE_MESSAGE);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !canSubmit) return;

    setProcessing(true);
    try {
      let result;
      const billingDetails = userEmail ? { email: userEmail } : undefined;
      const confirmParams = billingDetails
        ? { payment_method_data: { billing_details: billingDetails } }
        : undefined;

      if (setup) {
        result = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams,
        });
      } else {
        result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams,
        });
      }

      if (result.error) {
        if (isElementNotMountedError(result.error)) {
          reportHandledError(result.error, {
            category: "payment",
            feature: "checkout.confirm",
          });
          recoverLostElement();
        } else {
          onError(result.error.message || "Erro no pagamento");
        }
      } else {
        onComplete();
      }
    } catch (err: any) {
      reportHandledError(err, { category: "payment", feature: "checkout.confirm" });
      if (isElementNotMountedError(err)) {
        recoverLostElement();
      } else {
        onError(err.message || "Erro inesperado");
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className={`relative ${PAYMENT_ELEMENT_MIN_HEIGHT}`} aria-busy={!elementReady && !elementFailed}>
        {!elementReady && !elementFailed && (
          <div className="absolute inset-0"><PaymentFieldsSkeleton /></div>
        )}
        <div className={elementReady ? "visible" : "invisible"} aria-hidden={!elementReady}>
      <PaymentElement
        options={{
          layout: "tabs",
          fields: { billingDetails: { email: "never" } },
        }}
        onReady={() => setElementReady(true)}
        onLoadError={handleLoadError}
      />
        </div>
      </div>
      <Button type="submit" disabled={!canSubmit || processing} className="w-full gap-2">
        {processing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : setup ? (
          <>
            <CreditCard className="h-4 w-4" />
            Salvar cartão
          </>
        ) : (
          <>
            <Wallet className="h-4 w-4" />
            Confirmar pagamento
          </>
        )}
      </Button>
    </form>
  );
}

export default function Checkout({ setup }: CheckoutProps) {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [amountConfirmed, setAmountConfirmed] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const { isDarkMode } = useContext(ThemeContext);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  // Bumping the epoch remounts <Elements> (and the Payment Element iframe)
  // against the same clientSecret, recovering from a torn-down element
  // without forcing the user to reload the page.
  const [elementsEpoch, setElementsEpoch] = useState(0);
  const remountsUsed = useRef(0);
  useEffect(() => {
    stripePromise.then((s) => {
      if (!s) setStripeLoadFailed(true);
    });
  }, []);
  const {
    setPaymentStage,
    setPaymentToast,
    paymentOption,
    paymentMethod,
    setPaymentMethod,
    billingScope,
    activeOrganizationId,
  } = useContext(PaymentContext);

  const handleElementLost = () => {
    if (remountsUsed.current >= MAX_ELEMENT_REMOUNTS) return false;
    remountsUsed.current += 1;
    setElementsEpoch((epoch) => epoch + 1);
    return true;
  };

  const buildBillingUrl = (path: string, params: Record<string, string>) => {
    const search = new URLSearchParams(params);
    if (billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    return `${API_BASE}/api/billing${path}?${search.toString()}`;
  };

  const formatValue = (inputValue: string) => {
    inputValue = inputValue.replace(/\D/g, "");
    if (inputValue.length > 8) inputValue = inputValue.slice(0, 8);
    while (inputValue.length < 3) inputValue = "0" + inputValue;
    const reals = inputValue.slice(0, -2);
    const cents = inputValue.slice(-2);
    return `${parseInt(reals, 10)}.${cents}`;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatValue(e.target.value);
    if (parseFloat(formatted) > 30000) {
      setInputMessage("O valor máximo é R$ 30.000,00");
      setAmount("30000.00");
    } else {
      setInputMessage("");
      setAmount(formatted);
    }
  };

  const fetchClientSecret = async (amountValue?: string) => {
    setLoading(true);
    try {
      const paramsObj: Record<string, string> = {
        embedded: "true",
        save_card: setup.toString(),
      };
      if (paymentMethod !== "") paramsObj.payment_method = paymentMethod;
      if (!setup && amountValue) paramsObj.amount = amountValue;

      const url = buildBillingUrl("/checkout", paramsObj);
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.client_secret);
      } else {
        reportApiError(response, { feature: "checkout.intent", method: "GET" });
        setPaymentToast({
          type: "error",
          message: "Não foi possível iniciar o pagamento. Tente novamente.",
        });
        setPaymentStage("payment");
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "checkout.intent" });
      setPaymentToast({
        type: "error",
        message: "Erro durante o processamento. Tente novamente mais tarde.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (setup) fetchClientSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAmountSubmit = () => {
    const val = parseFloat(amount);
    if (isNaN(val) || val < 20) {
      setInputMessage("O valor mínimo é R$ 20,00");
      return;
    }
    setAmountConfirmed(true);
    fetchClientSecret(amount);
  };

  const handleComplete = () => {
    if (setup) {
      if (paymentOption === "single") {
        // The context's paymentMethod survives stage changes; a Pix pick
        // earlier in the modal would leak into the payment intent created
        // right after saving the card.
        setPaymentMethod("card");
        setPaymentToast({ type: "success", message: "Cartão adicionado com sucesso!" });
        setPaymentStage("payment");
      } else {
        setPaymentStage("methods");
      }
    } else {
      setPaymentToast({ type: "success", message: "Pagamento efetuado com sucesso!" });
    }
  };

  const handleError = (msg: string) => {
    setPaymentToast({ type: "error", message: msg });
  };

  if (!setup && !amountConfirmed) {
    return (
      <div className="flex flex-col gap-5 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground">
            <Wallet className="h-[18px] w-[18px]" />
          </span>
          <div>
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
              Adicionar créditos
            </h3>
            <p className="text-[13px] text-muted-foreground">
              Escolha o valor a ser carregado em seu saldo
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[13px] font-semibold text-foreground">Valor da recarga</label>
          <div className="flex items-center overflow-hidden rounded-md border border-border transition-colors focus-within:border-slate-400">
            <span className="border-r border-border bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground">
              R$
            </span>
            <input
              type="text"
              placeholder="0,00"
              value={formatAmountInput(amount)}
              onChange={handleAmountChange}
              autoFocus
              className="min-w-0 flex-1 bg-background px-3 py-2.5 text-base font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
            />
          </div>
          <MinimumAmountHint />
          {inputMessage && <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          {[20, 30, 50, 100, 200].map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => {
                setAmount(val.toFixed(2));
                setInputMessage("");
              }}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
            >
              R$ {val}
            </button>
          ))}
        </div>

        <Button onClick={handleAmountSubmit} className="w-full">
          Continuar para pagamento
        </Button>
      </div>
    );
  }

  if (loading || !clientSecret) {
    return (
      <div className="flex flex-col gap-5 p-6" aria-busy="true">
        <div className={PAYMENT_ELEMENT_MIN_HEIGHT}><PaymentFieldsSkeleton /></div>
        <Button disabled className="w-full gap-2">
          {setup ? <CreditCard className="h-4 w-4" /> : <Wallet className="h-4 w-4" />}
          {setup ? "Salvar cartão" : "Confirmar pagamento"}
        </Button>
      </div>
    );
  }

  if (stripeLoadFailed) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 text-center text-muted-foreground">
        <p>Não foi possível carregar o sistema de pagamentos.</p>
        <p className="text-[13px] opacity-70">
          Verifique se extensões do navegador (como bloqueadores de anúncios) estão impedindo o
          carregamento e tente novamente.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Elements
        key={elementsEpoch}
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: isDarkMode ? "night" : "stripe",
            variables: {
              colorPrimary: "#1a3c34",
              borderRadius: "8px",
              fontFamily: "Inter, system-ui, sans-serif",
              ...(isDarkMode
                ? {
                    colorBackground: "#0a0e1a",
                    colorText: "#e2e8f0",
                    colorTextSecondary: "#94a3b8",
                  }
                : {}),
            },
          },
        }}
      >
        <CheckoutForm
          setup={setup}
          onComplete={handleComplete}
          onError={handleError}
          onElementLost={handleElementLost}
          userEmail={user?.email}
        />
      </Elements>
    </div>
  );
}
